const FALLBACK_PALETTE = [
  "#ef4444", "#f97316", "#eab308", "#22c55e", "#14b8a6", "#3b82f6",
  "#a855f7", "#ec4899", "#84cc16", "#06b6d4", "#6366f1",
];

type BurstStyle = "peony" | "ring" | "willow" | "crackle" | "chrysanthemum";

const BURST_STYLES: BurstStyle[] = ["peony", "ring", "willow", "crackle", "chrysanthemum"];

interface LaunchOrigin {
  x: number;
  y: number;
}

interface PendingLaunch {
  at: number;
  origin: LaunchOrigin | null;
}

interface Rocket {
  x: number;
  y: number;
  px: number;
  py: number;
  vx: number;
  vy: number;
  targetY: number;
  colors: string[];
  style: BurstStyle;
  count: number;
}

interface Flash {
  type: "flash";
  x: number;
  y: number;
  life: number;
  decay: number;
  radius: number;
  color: string;
}

interface Spark {
  type: "spark";
  x: number;
  y: number;
  px: number;
  py: number;
  vx: number;
  vy: number;
  color: string;
  life: number;
  decay: number;
  gravity: number;
  drag: number;
  size: number;
  twinkle: boolean;
  trail: boolean;
  pop: number;
}

type Particle = Flash | Spark;

export class FireworksScene {
  private width = 0;
  private height = 0;
  private lastTime: number | null = null;
  private residueFrames = 0;
  private readonly rockets: Rocket[] = [];
  private readonly particles: Particle[] = [];
  private readonly pending: PendingLaunch[] = [];
  private palette: string[];

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly ctx: CanvasRenderingContext2D,
    palette: readonly string[] = FALLBACK_PALETTE,
  ) {
    this.palette = palette.length > 0 ? [...palette] : [...FALLBACK_PALETTE];
  }

  setPalette(palette: readonly string[]): void {
    this.palette = palette.length > 0 ? [...palette] : [...FALLBACK_PALETTE];
  }

  resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.width = rect.width;
    this.height = rect.height;
    this.canvas.width = Math.round(rect.width * dpr);
    this.canvas.height = Math.round(rect.height * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  launch(now: number, salvo = 3, origin: LaunchOrigin | null = null): void {
    const count = Math.max(1, Math.min(5, salvo));
    for (let i = 0; i < count; i += 1) {
      this.pending.push({
        at: now + i * 240 + (i === 0 ? 0 : this.random(0, 140)),
        origin,
      });
    }
    this.pending.sort((a, b) => a.at - b.at);
  }

  clear(): void {
    this.rockets.length = 0;
    this.particles.length = 0;
    this.pending.length = 0;
    this.residueFrames = 0;
    this.lastTime = null;
    this.ctx.clearRect(0, 0, this.width, this.height);
  }

  step(now: number): boolean {
    const dt = this.lastTime == null ? 1 : Math.min((now - this.lastTime) / 16.67, 3);
    this.lastTime = now;

    while (this.pending.length > 0 && this.pending[0]!.at <= now) {
      const launch = this.pending.shift()!;
      this.launchOne(launch.origin);
    }

    this.fadeCanvas();
    this.drawRockets(dt);
    this.drawParticles(dt);

    const active = this.pending.length > 0 || this.rockets.length > 0 || this.particles.length > 0;
    if (active) this.residueFrames = 12;
    else this.residueFrames -= 1;

    if (!active && this.residueFrames <= 0) {
      this.ctx.clearRect(0, 0, this.width, this.height);
      this.lastTime = null;
      return false;
    }
    return true;
  }

  private launchOne(origin: LaunchOrigin | null): void {
    const originX = origin ? Math.max(0, Math.min(this.width, origin.x)) : this.width / 2;
    const originY = origin
      ? Math.max(0, Math.min(this.height, origin.y))
      : this.height - Math.min(90, this.height * 0.12);
    const travel = Math.max(80, this.height * this.random(0.16, 0.38));
    const colors = [this.pick(this.palette)];
    if (Math.random() < 0.55) colors.push(this.pick(this.palette));
    this.rockets.push({
      x: originX + this.random(-14, 14),
      y: originY,
      px: originX,
      py: originY,
      vx: this.random(-0.9, 0.9),
      vy: -this.random(10, 13.5) * Math.max(this.height / 760, 0.55),
      targetY: Math.max(24, originY - travel),
      colors,
      style: this.pick(BURST_STYLES),
      count: Math.floor(this.random(70, 150)),
    });
  }

  private explode(rocket: Rocket): void {
    const { x, y, colors, style } = rocket;
    const count = style === "willow" ? Math.floor(rocket.count * 0.7) : rocket.count;
    const base = this.random(4.2, 6.8);
    const tilt = this.random(0, Math.PI);
    this.particles.push({
      type: "flash", x, y, life: 1, decay: 0.12,
      radius: this.random(40, 70), color: colors[0]!,
    });
    for (let i = 0; i < count; i += 1) {
      const angle = style === "ring" ? (i / count) * Math.PI * 2 : Math.random() * Math.PI * 2;
      let speed = base * Math.sqrt(Math.random());
      if (style === "ring") speed = base * this.random(0.96, 1.04);
      if (style === "willow") speed = base * this.random(0.3, 0.75);
      let vx = Math.cos(angle) * speed;
      let vy = Math.sin(angle) * speed;
      if (style === "ring") {
        const cos = Math.cos(tilt);
        const sin = Math.sin(tilt);
        const rotatedX = vx * cos - vy * sin;
        const rotatedY = (vx * sin + vy * cos) * this.random(0.55, 0.8);
        vx = rotatedX;
        vy = rotatedY;
      }
      this.particles.push({
        type: "spark", x, y, px: x, py: y, vx, vy,
        color: colors[i % colors.length]!, life: 1,
        decay: style === "willow" ? this.random(0.004, 0.007) : this.random(0.008, 0.016),
        gravity: style === "willow" ? 0.085 : 0.045,
        drag: style === "willow" ? 0.985 : 0.965,
        size: this.random(1.4, 2.6), twinkle: Math.random() < 0.6,
        trail: style === "chrysanthemum",
        pop: style === "crackle" && Math.random() < 0.5 ? this.random(0.35, 0.6) : 0,
      });
    }
  }

  private fadeCanvas(): void {
    this.ctx.globalCompositeOperation = "destination-out";
    this.ctx.fillStyle = "rgba(0,0,0,0.16)";
    this.ctx.fillRect(0, 0, this.width, this.height);
    this.ctx.globalCompositeOperation = "lighter";
    this.ctx.lineCap = "round";
  }

  private drawRockets(dt: number): void {
    for (let i = this.rockets.length - 1; i >= 0; i -= 1) {
      const rocket = this.rockets[i]!;
      rocket.px = rocket.x;
      rocket.py = rocket.y;
      rocket.x += rocket.vx * dt;
      rocket.y += rocket.vy * dt;
      rocket.vy += 0.09 * dt;
      this.stroke(rocket.px, rocket.py, rocket.x, rocket.y, rocket.colors[0]!, 2.4);
      if (Math.random() < 0.8) this.particles.push(this.rocketSpark(rocket));
      if (rocket.y <= rocket.targetY || rocket.vy >= -1.6) {
        this.rockets.splice(i, 1);
        this.explode(rocket);
      }
    }
  }

  private drawParticles(dt: number): void {
    for (let i = this.particles.length - 1; i >= 0; i -= 1) {
      const particle = this.particles[i]!;
      particle.life -= particle.decay * dt;
      if (particle.life <= 0) {
        this.particles.splice(i, 1);
        continue;
      }
      if (particle.type === "flash") {
        const radius = particle.radius * (1.6 - particle.life * 0.6);
        const gradient = this.ctx.createRadialGradient(particle.x, particle.y, 0, particle.x, particle.y, radius);
        gradient.addColorStop(0, particle.color);
        gradient.addColorStop(1, "rgba(0,0,0,0)");
        this.ctx.globalAlpha = particle.life * 0.32;
        this.ctx.fillStyle = gradient;
        this.ctx.beginPath();
        this.ctx.arc(particle.x, particle.y, radius, 0, Math.PI * 2);
        this.ctx.fill();
        this.ctx.globalAlpha = 1;
        continue;
      }
      this.advanceSpark(particle, dt);
      if (particle.pop > 0 && particle.life < particle.pop) {
        particle.life = 0;
        this.spawnCrackle(particle);
        continue;
      }
      const alpha = particle.life * (particle.twinkle ? this.random(0.45, 1) : 1);
      this.ctx.globalAlpha = Math.max(alpha, 0);
      this.stroke(
        particle.px, particle.py, particle.x, particle.y,
        particle.color, particle.size * (particle.trail ? 1.4 : 1),
      );
      this.ctx.globalAlpha = 1;
    }
    if (this.particles.length > 1200) this.particles.splice(0, this.particles.length - 1200);
  }

  private advanceSpark(spark: Spark, dt: number): void {
    spark.px = spark.x;
    spark.py = spark.y;
    spark.vx *= Math.pow(spark.drag, dt);
    spark.vy = spark.vy * Math.pow(spark.drag, dt) + spark.gravity * dt;
    spark.x += spark.vx * dt;
    spark.y += spark.vy * dt;
  }

  private spawnCrackle(spark: Spark): void {
    for (let i = 0; i < 6; i += 1) {
      const angle = Math.random() * Math.PI * 2;
      const speed = this.random(0.8, 2.2);
      this.particles.push({
        type: "spark", x: spark.x, y: spark.y, px: spark.x, py: spark.y,
        vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed,
        color: spark.color, life: 0.6, decay: 0.03, gravity: 0.05,
        drag: 0.96, size: 1.2, twinkle: true, trail: false, pop: 0,
      });
    }
  }

  private rocketSpark(rocket: Rocket): Spark {
    return {
      type: "spark", x: rocket.x + this.random(-1.5, 1.5), y: rocket.y + this.random(0, 4),
      px: rocket.x, py: rocket.y, vx: this.random(-0.4, 0.4), vy: this.random(0.2, 1),
      color: rocket.colors[1] ?? rocket.colors[0]!, life: 0.5, decay: 0.05, gravity: 0.02,
      drag: 0.96, size: 1.1, twinkle: false, trail: false, pop: 0,
    };
  }

  private stroke(x1: number, y1: number, x2: number, y2: number, color: string, width: number): void {
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = width;
    this.ctx.beginPath();
    this.ctx.moveTo(x1, y1);
    this.ctx.lineTo(x2, y2);
    this.ctx.stroke();
  }

  private random(min: number, max: number): number {
    return min + Math.random() * (max - min);
  }

  private pick<T>(items: T[]): T {
    return items[Math.floor(Math.random() * items.length)]!;
  }
}
